import React, { useId } from 'react';
import { Check, Gift } from 'lucide-react';
import type { StorefrontUpsell } from '@xshop/shared';
import { resolveUpsellBumpLabels, type UpsellBumpLabels } from './upsellBumpLabels';
import './upsellBump.css';
import { responsive, THUMB_SIZES } from '../elements/render/responsiveImage';

interface UpsellBumpProps {
  offer: StorefrontUpsell;
  accepted: boolean;
  format: (value: number) => string;
  onToggle: (accepted: boolean) => void;
  /** A theme or an element in another language rewords these; passing none keeps today's wording. */
  labels?: Partial<UpsellBumpLabels>;
  showProductImage?: boolean;
  showCompareAt?: boolean;
}

interface UpsellPriceProps {
  offer: StorefrontUpsell;
  format: (value: number) => string;
  savingsPrefix: string;
  showCompareAt: boolean;
}

// The offer's own money, exactly as the server priced it; nothing here multiplies or discounts anything.
const UpsellPrice: React.FC<UpsellPriceProps> = ({ offer, format, savingsPrefix, showCompareAt }) => (
  <div className="upsell-bump-prices">
    <span className="upsell-bump-price">{format(offer.unitPrice)}</span>
    {showCompareAt && offer.compareAtPrice !== null && offer.compareAtPrice > offer.unitPrice && (
      <span className="upsell-bump-compare">{format(offer.compareAtPrice)}</span>
    )}
    {offer.savings > 0 && <span className="upsell-bump-savings">{savingsPrefix} {format(offer.savings)}</span>}
  </div>
);

// What the offer itself says: its own name, the merchant's line under it, and which product it is.
const UpsellCopy: React.FC<{ offer: StorefrontUpsell; optIn: string }> = ({ offer, optIn }) => (
  <div className="upsell-bump-text">
    <span className="upsell-bump-title" dir="auto">
      <Gift size={15} aria-hidden="true" />
      {offer.title}
    </span>
    {offer.description && <span className="upsell-bump-desc" dir="auto">{offer.description}</span>}
    <span className="upsell-bump-product" dir="auto">
      {offer.productTitle}{offer.variantTitle ? ` — ${offer.variantTitle}` : ''}
    </span>
    {optIn && <span className="upsell-bump-opt" dir="auto">{optIn}</span>}
  </div>
);

// The optional one-tap add inside the COD form: ticking it puts the offer on the order at the price shown.
export const UpsellBump: React.FC<UpsellBumpProps> = ({
  offer, accepted, format, onToggle, labels, showProductImage = true, showCompareAt = true
}) => {
  const words = resolveUpsellBumpLabels(labels);
  // One id per instance, so two bumps on the same page no longer both answer to `upsell-bump-accept`.
  const inputId = useId();
  return (
    <div className={`upsell-bump ${accepted ? 'accepted' : ''}`}>
      {words.heading && <span className="upsell-bump-heading" dir="auto">{words.heading}</span>}
      {words.pitch && <span className="upsell-bump-pitch" dir="auto">{words.pitch}</span>}
      <label className="upsell-bump-main" htmlFor={inputId}>
        <input
          id={inputId}
          type="checkbox"
          className="upsell-bump-check"
          checked={accepted}
          onChange={(event) => onToggle(event.target.checked)}
        />
        {showProductImage && offer.imageUrl && <img className="upsell-bump-thumb" {...responsive(offer.imageUrl, THUMB_SIZES)} alt="" />}
        <UpsellCopy offer={offer} optIn={words.checkboxLabel} />
        <UpsellPrice offer={offer} format={format} savingsPrefix={words.savingsPrefix} showCompareAt={showCompareAt} />
      </label>
      {accepted && (
        <div className="upsell-bump-confirm">
          <Check size={14} aria-hidden="true" />
          <span dir="auto">{words.acceptedText}</span>
        </div>
      )}
    </div>
  );
};
